import { ByEntityId } from '@upright/common/model/input/types';
import { getItemList, getKTImpact, getKTValueSet } from '..';
import { friendlyImpactTitle } from '../../../../../utils';
import { predefinedValueSets } from '../../../../input/value-sets/value-sets';
import {
  pickShowcaseCategory,
  SHOWCASE_EXCLUDED_NEGATIVE_CATEGORIES,
  SHOWCASE_EXCLUDED_POSITIVE_CATEGORIES,
} from '../showcase-categories';
import { KeyTakeawayType } from '../types';
import {
  WeightSetKeyTakeaway,
  WeightSetKeyTakeawayBestValueSet,
  WeightSetKeyTakeawayBiggestNegative,
  WeightSetKeyTakeawayBiggestPositive,
} from './types-weight-set-key-takeaway';

export interface CompanyGroupKeyTakeawayProps {
  takeaway: WeightSetKeyTakeaway;
  groupName: string | undefined;
  itemCount: number;
  namesById: ByEntityId<string | undefined>;
}

export function getCompanyGroupBiggestPositiveTakeaway(props: CompanyGroupKeyTakeawayProps) {
  if (props.takeaway.type !== KeyTakeawayType.BIGGEST_POSITIVE) {
    return '';
  }

  const takeaway: WeightSetKeyTakeawayBiggestPositive = props.takeaway;
  const impactCategories = takeaway.impactCategories;
  const friendlyImpactCategories = impactCategories.map(d => friendlyImpactTitle(d.impactCategory));
  const showcaseImpact = pickShowcaseCategory(impactCategories, SHOWCASE_EXCLUDED_POSITIVE_CATEGORIES);

  if (!showcaseImpact) {
    return 'No key takeaway available';
  }

  const showcaseItemIds = showcaseImpact.contributingWeightSetItems.map(d => d.entityId);
  const showcaseIsLargest = showcaseImpact.impactCategory === impactCategories[0].impactCategory;
  const friendlyShowcase = friendlyImpactTitle(showcaseImpact.impactCategory);

  return `
      ${props.groupName || 'The company group'} seems to create the most significant positive value in categories${' '}
      ${getKTImpact(friendlyImpactCategories[0])}, ${getKTImpact(friendlyImpactCategories[1])}, and${' '}
      ${getKTImpact(friendlyImpactCategories[2])}.${' '}
      ${
        showcaseIsLargest
          ? `
          The largest positive contribution comes from its ${getKTImpact(friendlyShowcase)} impact, with the
          largest drivers being
        `
          : `
          ${' '}
          The positive contribution in the ${getKTImpact(friendlyShowcase)} impact category is
          ${showcaseItemIds.length > 3 ? ' mostly ' : ' '}driven by
        `
      }${' '}
      companies${' '}
      ${getItemList(showcaseItemIds, props.namesById)}.
    `;
}

export function getCompanyGroupBiggestNegativeTakeaway(props: CompanyGroupKeyTakeawayProps) {
  if (props.takeaway.type !== KeyTakeawayType.BIGGEST_NEGATIVE) {
    return '';
  }

  const takeaway: WeightSetKeyTakeawayBiggestNegative = props.takeaway;

  const impactCategories = takeaway.impactCategories;

  const friendlyImpactCategories = impactCategories.map(d => friendlyImpactTitle(d.impactCategory));
  const showcaseImpact = pickShowcaseCategory(impactCategories, SHOWCASE_EXCLUDED_NEGATIVE_CATEGORIES);

  if (!showcaseImpact) {
    return 'No key takeaway available';
  }

  const showcaseItemIds = showcaseImpact.contributingWeightSetItems.map(d => d.entityId);

  const showcaseIsLargest = showcaseImpact.impactCategory === impactCategories[0].impactCategory;
  const friendlyShowcase = friendlyImpactTitle(showcaseImpact.impactCategory);

  return `
    ${props.groupName || 'The company group'} uses resources or causes negative impacts mostly in
     categories${' '}
     ${getKTImpact(friendlyImpactCategories[0])}, ${getKTImpact(friendlyImpactCategories[1])},
      and ${getKTImpact(friendlyImpactCategories[2])}.${' '}
      ${
        showcaseIsLargest
          ? `
          The largest negative impact of ${props.groupName || 'the company group'} in category ${getKTImpact(
            friendlyShowcase,
          )}
        `
          : `
          The negative contribution in the ${getKTImpact(friendlyShowcase)} impact category
        `
      }${' '}
      ${props.itemCount > 3 ? `seems to be driven mostly` : `is driven`} by${' '}
      ${showcaseItemIds.length > 1 ? ` companies` : ` company`}${' '}
      ${getItemList(showcaseItemIds, props.namesById)}.
    `;
}
export function getCompanyGroupBestValuesetTakeaway(props: CompanyGroupKeyTakeawayProps) {
  if (props.takeaway.type !== KeyTakeawayType.BEST_VALUE_SET) {
    return ``;
  }

  const takeaway: WeightSetKeyTakeawayBestValueSet = props.takeaway;
  const valueSet = predefinedValueSets.find(d => d.id === takeaway.valueSetName);
  const valueSetText = valueSet?.keyTakeawayText;

  return `
      Currently the net impact profile of ${
        props.groupName || 'the company group'
      } seems to best serve stakeholders who prioritize ${' '}
      ${getKTValueSet(valueSetText)} over other priorities.
    `;
}
